import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  accessLogoutUrl,
  api,
  ApiError,
  type RoomListItem,
  type VideoListItem,
} from '../api/client.ts';
import { MAX_CONCURRENT_ROOMS } from '@syncstream/protocol';

function formatDuration(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`;
}

export function DashboardPage() {
  const [videos, setVideos] = useState<VideoListItem[] | null>(null);
  const [rooms, setRooms] = useState<RoomListItem[] | null>(null);
  const [name, setName] = useState('');
  const [videoId, setVideoId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const [v, r] = await Promise.all([api.listVideos(), api.listRooms()]);
      setVideos(v.items);
      setRooms(r.items);
      if (v.items.length > 0 && !videoId && v.items[0]) setVideoId(v.items[0].id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Access session expired — bounce through CF Access to re-auth.
        window.location.assign(accessLogoutUrl());
        return;
      }
      setError((err as Error).message);
    }
  }, [videoId]);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 5000);
    return () => clearInterval(t);
  }, [refresh]);

  async function onCreateRoom(e: React.FormEvent) {
    e.preventDefault();
    if (!name || !videoId) return;
    setError(null);
    try {
      const { id } = await api.createRoom(name, videoId);
      navigate(`/room/${id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(`Max ${MAX_CONCURRENT_ROOMS} active rooms.`);
      } else {
        setError((err as Error).message);
      }
    }
  }

  function onLogout() {
    window.location.assign(accessLogoutUrl());
  }

  async function onCloseRoom(id: string) {
    await api.closeRoom(id);
    await refresh();
  }

  return (
    <div className="min-h-screen p-6 max-w-5xl mx-auto">
      <header className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-semibold">syncstream</h1>
        <button
          type="button"
          onClick={onLogout}
          className="px-3 py-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 rounded"
        >
          Sign out
        </button>
      </header>

      {error && (
        <div
          role="alert"
          className="mb-4 px-3 py-2 bg-red-950 border border-red-700 rounded text-sm"
        >
          {error}
        </div>
      )}

      {/* Active rooms */}
      <section className="mb-10">
        <h2 className="text-lg font-medium mb-3">Active rooms</h2>
        {rooms == null ? (
          <p className="text-sm text-zinc-500">Loading…</p>
        ) : rooms.length === 0 ? (
          <p className="text-sm text-zinc-500">No active rooms.</p>
        ) : (
          <ul className="space-y-2">
            {rooms.map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between bg-zinc-900 rounded px-4 py-3"
              >
                <div>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-zinc-500">
                    {r.video_title ?? r.video_id} · {r.member_count} viewer
                    {r.member_count === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/room/${r.id}`)}
                    className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 rounded"
                  >
                    Join
                  </button>
                  <button
                    type="button"
                    onClick={() => void onCloseRoom(r.id)}
                    className="px-3 py-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 rounded"
                  >
                    Close
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Create form */}
        {videos && videos.length > 0 && (
          <form onSubmit={onCreateRoom} className="mt-4 flex gap-2 items-end flex-wrap">
            <label className="flex-1 min-w-[160px]">
              <span className="block text-xs text-zinc-400 mb-1">Room name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 rounded outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
            <label className="flex-1 min-w-[160px]">
              <span className="block text-xs text-zinc-400 mb-1">Video</span>
              <select
                value={videoId}
                onChange={(e) => setVideoId(e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 rounded outline-none focus:ring-2 focus:ring-blue-500"
              >
                {videos.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.title}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm font-medium"
            >
              Create room
            </button>
          </form>
        )}
      </section>

      {/* Library */}
      <section>
        <h2 className="text-lg font-medium mb-3">Library</h2>
        {videos == null ? (
          <p className="text-sm text-zinc-500">Loading…</p>
        ) : videos.length === 0 ? (
          <p className="text-sm text-zinc-500">
            No videos yet. Ingest one with the CLI:{' '}
            <code>pnpm ingest --input ./movie.mp4 --title "Movie"</code>
          </p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map((v) => (
              <li key={v.id} className="bg-zinc-900 rounded overflow-hidden">
                {v.poster_url ? (
                  <img
                    src={v.poster_url}
                    alt={`Poster for ${v.title}`}
                    className="w-full aspect-video object-cover"
                  />
                ) : (
                  <div className="w-full aspect-video bg-zinc-800 flex items-center justify-center text-xs text-zinc-500">
                    no poster
                  </div>
                )}
                <div className="p-3">
                  <div className="font-medium">{v.title}</div>
                  <div className="text-xs text-zinc-500">
                    {formatDuration(v.duration_seconds)} · {Math.round(v.bytes / 1024 / 1024)} MB
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
